import React from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { motion } from 'framer-motion';
import { useMapBearing, useMapZoom } from './MapBearingContext';
import type { DetectorDefinition } from '../../types/intersection';

interface DetectorMarkerProps {
  detector: DetectorDefinition;
  active: boolean;
}

export const DetectorMarker = React.memo(function DetectorMarker({
  detector,
  active
}: DetectorMarkerProps) {
  const mapBearing = useMapBearing();
  const mapZoom = useMapZoom();
  const scale = Math.min(2, Math.max(0.3, Math.pow(2, (mapZoom - 18.5) * 0.5)));
  const isLoop = detector.type === 'loop';
  const typeLabel = isLoop ? 'Espira' : 'Pulsador';

  return (
    <Marker
      longitude={detector.position[0]}
      latitude={detector.position[1]}
      anchor="center"
    >
      <div className="relative group/det cursor-pointer flex flex-col items-center" style={{ transform: `scale(${scale})` }}>
        {isLoop ? (
          /* Espira inductiva */
          <div className="relative">
            {/* Glow */}
            <motion.div
              className="absolute -inset-1.5 rounded-md"
              style={{ backgroundColor: active ? 'rgba(34, 211, 238, 0.3)' : 'rgba(34, 211, 238, 0.1)' }}
              animate={active ? { opacity: [0.5, 1, 0.5] } : {}}
              transition={active ? { duration: 1.5, repeat: Infinity } : {}}
            />
            <motion.div
              className="relative rounded-sm border-2"
              style={{
                width: 24,
                height: 16,
                borderColor: active ? '#22d3ee' : '#67e8f9',
                backgroundColor: active ? 'rgba(34, 211, 238, 0.3)' : 'rgba(34, 211, 238, 0.1)',
                transform: `rotate(${(detector.bearing ?? 0) - mapBearing}deg)`,
                boxShadow: active ? '0 0 8px rgba(34, 211, 238, 0.5)' : '0 0 4px rgba(34, 211, 238, 0.2)',
              }}
              animate={active ? { borderColor: ['#22d3ee', '#06b6d4', '#22d3ee'] } : {}}
              transition={active ? { duration: 1.5, repeat: Infinity } : {}}
            >
              {/* Líneas interiores simulando espira */}
              <div className="absolute inset-[3px] border border-cyan-400/40 rounded-[1px]" />
            </motion.div>
          </div>
        ) : (
          /* Pulsador peatonal */
          <div className="relative">
            {/* Glow */}
            <motion.div
              className="absolute -inset-2 rounded-full"
              style={{ backgroundColor: active ? 'rgba(250, 204, 21, 0.3)' : 'rgba(250, 204, 21, 0.1)' }}
              animate={active ? { opacity: [0.5, 1, 0.5] } : {}}
              transition={active ? { duration: 1, repeat: Infinity } : {}}
            />
            <motion.div
              className="relative rounded-full border-2 flex items-center justify-center"
              style={{
                width: 18,
                height: 18,
                borderColor: active ? '#facc15' : '#fde047',
                backgroundColor: active ? 'rgba(250, 204, 21, 0.3)' : 'rgba(250, 204, 21, 0.1)',
                boxShadow: active ? '0 0 8px rgba(250, 204, 21, 0.5)' : '0 0 4px rgba(250, 204, 21, 0.2)',
              }}
              animate={active ? { scale: [1, 1.2, 1] } : {}}
              transition={active ? { duration: 1, repeat: Infinity } : {}}
            >
              {/* Punto central */}
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: active ? '#facc15' : '#fde047' }} />
            </motion.div>
          </div>
        )}
        {/* Etiqueta siempre visible */}
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none">
          <span className="text-[9px] font-mono bg-gray-900/80 text-gray-300 px-1 py-px rounded">
            {typeLabel} · {detector.label}
          </span>
        </div>
      </div>
    </Marker>
  );
});
